<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappBillingAPI from 'dashboard/api/whatsappBilling';

const { t } = useI18n();

const isLoading = ref(true);
const isSyncing = ref(false);
const selectedInboxId = ref(null);
const billingData = ref({
  current_balance: { amount: 0, currency: 'AUD', formatted: 'A$0,00' },
  paying_with: null,
  payment_methods: [],
  payment_activities: [],
  business_info: { name: '', address: '', currency: '' },
  meta_links: {},
  available_inboxes: [],
});

const fetchBillingData = async (inboxId = null) => {
  try {
    isLoading.value = true;
    const response = await WhatsappBillingAPI.get(inboxId);
    if (response.data) {
      billingData.value = response.data;
      if (!selectedInboxId.value && response.data.inbox_id) {
        selectedInboxId.value = response.data.inbox_id;
      }
    }
  } catch (error) {
    // Keep existing or fallback state
  } finally {
    isLoading.value = false;
    isSyncing.value = false;
  }
};

const syncFromMeta = () => {
  isSyncing.value = true;
  fetchBillingData(selectedInboxId.value);
};

const onSelectInbox = inboxId => {
  if (selectedInboxId.value === inboxId) return;
  selectedInboxId.value = inboxId;
  fetchBillingData(inboxId);
};

const currentBalance = computed(() => billingData.value.current_balance || {});
const breakdown = computed(() => billingData.value.breakdown || []);
const paymentActivities = computed(
  () => billingData.value.payment_activities || []
);
const businessInfo = computed(() => billingData.value.business_info || {});
const metaLinks = computed(() => billingData.value.meta_links || {});
const availableInboxes = computed(
  () => billingData.value.available_inboxes || []
);

const openMetaLink = url => {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const formatResetDate = dateStr => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString();
  } catch (e) {
    return dateStr;
  }
};

onMounted(() => {
  fetchBillingData();
});
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <ReportHeader
      :header-title="t('PAYMENT_ACTIVITY.HEADER')"
      :header-description="t('PAYMENT_ACTIVITY.DESCRIPTION')"
    >
      <div class="flex items-center gap-3">
        <NextButton
          size="sm"
          outline
          slate
          icon="i-lucide-refresh-cw"
          :is-loading="isSyncing"
          @click="syncFromMeta"
        >
          {{ t('PAYMENT_ACTIVITY.SYNC_BUTTON') }}
        </NextButton>
      </div>
    </ReportHeader>

    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <div
      v-else
      class="bg-white border shadow-sm rounded-xl dark:bg-n-solid-2 border-n-slate-6 dark:border-n-slate-6 divide-y divide-n-slate-6 dark:divide-n-slate-6 overflow-hidden"
    >
      <!-- 1. Sector: Monto a pagar y desglose -->
      <section class="p-6">
        <!-- Pestañas de líneas telefónicas si la empresa tiene más de 1 número -->
        <div
          v-if="availableInboxes.length > 1"
          class="flex flex-wrap items-center gap-2 pb-4 mb-5 border-b border-n-slate-6 dark:border-n-slate-6"
        >
          <span class="mr-1 text-xs font-semibold text-n-slate-11">
            {{ t('PAYMENT_ACTIVITY.SELECT_INBOX') }}{{ ':' }}
          </span>
          <button
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer"
            :class="
              selectedInboxId == inbox.id
                ? 'bg-n-brand text-white shadow-sm'
                : 'bg-n-alpha-1 dark:bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 border border-n-slate-6 dark:border-n-slate-6'
            "
            @click="onSelectInbox(inbox.id)"
          >
            <span class="i-lucide-phone size-3" />
            <span>{{ inbox.name }}</span>
            <span class="opacity-80">
              ({{ inbox.phone_number || inbox.id }})
            </span>
          </button>
        </div>

        <div class="flex items-start justify-between">
          <div class="flex flex-col gap-2">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.TITLE') }}
              </span>
              <span
                v-if="billingData.waba_id"
                class="px-2 py-0.5 text-[10px] font-mono font-medium rounded bg-n-alpha-1 dark:bg-n-solid-1 text-n-slate-11 border border-n-slate-6 dark:border-n-slate-6"
              >
                {{ 'WABA: ' }}{{ billingData.waba_id }}
              </span>
            </div>
            <div class="text-4xl font-normal tracking-tight text-n-slate-12">
              {{ currentBalance.formatted || '$0,00' }}
            </div>
          </div>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium transition-colors border rounded-lg bg-transparent text-n-slate-12 border-n-slate-6 hover:bg-n-alpha-1 active:bg-n-alpha-2 cursor-pointer"
            @click="openMetaLink(metaLinks.pay_url)"
          >
            {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.PAY_BUTTON') }}
          </button>
        </div>

        <div
          v-if="currentBalance.amount === 0"
          class="flex items-center gap-1.5 mt-3 text-xs font-medium text-n-teal-11"
        >
          <span class="size-2 rounded-full bg-n-teal-9" />
          {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.UP_TO_DATE') }}
        </div>

        <div
          v-if="billingData.last_reset_at"
          class="flex items-center gap-1.5 mt-2 text-xs text-n-slate-11"
        >
          <span class="i-lucide-check-circle-2 size-3.5 text-n-teal-9" />
          <span>{{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.LAST_RESET') }}: </span>
          <span class="font-medium text-n-slate-12">
            {{ formatResetDate(billingData.last_reset_at) }}
          </span>
        </div>

        <!-- Tabla de desglose por categoría -->
        <div
          v-if="breakdown.length > 0"
          class="pt-5 mt-6 border-t border-n-slate-6 dark:border-n-slate-6"
        >
          <h4
            class="mb-3 text-xs font-semibold uppercase tracking-wider text-n-slate-11"
          >
            {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.BREAKDOWN_TITLE') }}
          </h4>
          <div class="w-full overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="border-b border-n-slate-6 dark:border-n-slate-6">
                  <th class="pb-2.5 text-xs font-semibold text-n-slate-11">
                    {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.TABLE.CATEGORY') }}
                  </th>
                  <th class="pb-2.5 text-xs font-semibold text-n-slate-11">
                    {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.TABLE.MESSAGES') }}
                  </th>
                  <th
                    class="pb-2.5 text-xs font-semibold text-right text-n-slate-11"
                  >
                    {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.TABLE.SUBTOTAL') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-slate-6 dark:divide-n-slate-6">
                <tr
                  v-for="item in breakdown"
                  :key="item.key"
                  class="hover:bg-n-alpha-1 transition-colors"
                >
                  <td class="py-2.5 text-sm font-medium text-n-slate-12">
                    <div class="flex items-center gap-2">
                      <span
                        class="size-2 rounded-full flex-shrink-0"
                        :class="{
                          'bg-n-brand': item.key === 'marketing',
                          'bg-n-amber-9': item.key === 'utility',
                          'bg-n-blue-9': item.key === 'authentication',
                          'bg-n-teal-9': item.key === 'service',
                        }"
                      />
                      <span>
                        {{
                          t(
                            `PAYMENT_ACTIVITY.CURRENT_BALANCE.CATEGORIES.${item.key.toUpperCase()}`
                          )
                        }}
                      </span>
                    </div>
                  </td>
                  <td class="py-2.5 text-sm text-n-slate-12">
                    {{ item.count.toLocaleString() }}
                  </td>
                  <td
                    class="py-2.5 text-sm font-medium text-right text-n-slate-12"
                  >
                    {{ item.formatted_subtotal }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="border-t-2 border-n-slate-6 dark:border-n-slate-6">
                  <td
                    colspan="2"
                    class="pt-3 text-sm font-semibold text-n-slate-12"
                  >
                    {{ t('PAYMENT_ACTIVITY.CURRENT_BALANCE.TOTAL_LABEL') }}
                  </td>
                  <td class="pt-3 text-sm font-bold text-right text-n-slate-12">
                    {{ currentBalance.formatted || '$0,00' }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      <!-- 2. Sector: Actividad de pago -->
      <section class="p-6">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ t('PAYMENT_ACTIVITY.ACTIVITY.TITLE') }}
        </h3>

        <div class="w-full overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="border-b border-n-slate-6 dark:border-n-slate-6">
                <th class="pb-3 text-xs font-semibold text-n-slate-11">
                  {{ t('PAYMENT_ACTIVITY.ACTIVITY.TABLE.DATE') }}
                </th>
                <th class="pb-3 text-xs font-semibold text-n-slate-11">
                  {{ t('PAYMENT_ACTIVITY.ACTIVITY.TABLE.AMOUNT') }}
                </th>
                <th class="pb-3 text-xs font-semibold text-n-slate-11">
                  {{ t('PAYMENT_ACTIVITY.ACTIVITY.TABLE.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-slate-6 dark:divide-n-slate-6">
              <tr
                v-for="activity in paymentActivities"
                :key="activity.id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <td class="py-3.5 text-sm text-n-slate-12">
                  {{ activity.formatted_date || activity.date }}
                </td>
                <td class="py-3.5 text-sm font-medium text-n-slate-12">
                  {{ activity.formatted_amount }}
                </td>
                <td class="py-3.5 text-sm">
                  <div class="inline-flex items-center gap-2">
                    <span
                      class="size-2 rounded-full flex-shrink-0"
                      :class="{
                        'bg-n-ruby-9': activity.status === 'error',
                        'bg-n-teal-9': activity.status === 'success',
                        'bg-n-amber-9': activity.status === 'pending',
                      }"
                    />
                    <span
                      class="font-medium"
                      :class="{
                        'text-n-ruby-11': activity.status === 'error',
                        'text-n-teal-11': activity.status === 'success',
                        'text-n-amber-11': activity.status === 'pending',
                        'text-n-slate-11': ![
                          'error',
                          'success',
                          'pending',
                        ].includes(activity.status),
                      }"
                    >
                      {{
                        t(
                          `PAYMENT_ACTIVITY.ACTIVITY.STATUSES.${activity.status.toUpperCase()}`,
                          activity.status_label || activity.status
                        )
                      }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="paymentActivities.length === 0">
                <td
                  colspan="3"
                  class="py-6 text-xs text-center text-n-slate-11"
                >
                  {{ t('PAYMENT_ACTIVITY.ACTIVITY.NO_ACTIVITY') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pt-4 mt-2">
          <button
            type="button"
            class="text-sm font-medium transition-colors text-n-brand hover:underline cursor-pointer bg-transparent border-0 p-0"
            @click="openMetaLink(metaLinks.all_activity_url)"
          >
            {{ t('PAYMENT_ACTIVITY.ACTIVITY.VIEW_ALL') }}
          </button>
        </div>
      </section>

      <!-- 3. Sector: Información del negocio -->
      <section class="p-6">
        <div class="flex items-center justify-between pb-4">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('PAYMENT_ACTIVITY.BUSINESS_INFO.TITLE') }}
          </h3>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium transition-colors border rounded-lg bg-transparent text-n-slate-12 border-n-slate-6 hover:bg-n-alpha-1 active:bg-n-alpha-2 cursor-pointer"
            @click="openMetaLink(metaLinks.edit_business_url)"
          >
            {{ t('PAYMENT_ACTIVITY.BUSINESS_INFO.EDIT_BUTTON') }}
          </button>
        </div>

        <div
          class="grid grid-cols-1 gap-6 pt-2"
          :class="businessInfo.address ? 'md:grid-cols-3' : 'md:grid-cols-2'"
        >
          <div class="flex flex-col gap-1">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ t('PAYMENT_ACTIVITY.BUSINESS_INFO.NAME') }}
            </span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ businessInfo.name || '-' }}
            </span>
          </div>

          <div v-if="businessInfo.address" class="flex flex-col gap-1">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ t('PAYMENT_ACTIVITY.BUSINESS_INFO.ADDRESS') }}
            </span>
            <span class="text-sm text-n-slate-12 leading-relaxed break-words">
              {{ businessInfo.address }}
            </span>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-xs font-semibold text-n-slate-11">
              {{ t('PAYMENT_ACTIVITY.BUSINESS_INFO.CURRENCY') }}
            </span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ businessInfo.currency || '-' }}
            </span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
